import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import { Play, X, ArrowRight, Search } from "lucide-react";
import { FILM_LENGTHS, FILM_GENRES } from "../constants/film";

const LENGTHS = ["All", ...FILM_LENGTHS];

function FilmCard({ film, onPlay }) {
  const thumb = `https://i.ytimg.com/vi/${film.youtube_id}/hqdefault.jpg`;
  return (
    <article className="bg-white border-2 border-[#0A0A0A] flex flex-col" data-testid={`film-card-${film.id}`}>
      <button
        type="button"
        onClick={() => onPlay(film)}
        data-testid={`film-play-${film.id}`}
        className="relative group block w-full aspect-video overflow-hidden border-b border-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#FF2E63]"
        aria-label={`Play ${film.title}`}
      >
        <img src={thumb} alt={`Still from ${film.title}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" loading="lazy" />
        <div className="absolute inset-0 bg-[#0A0A0A]/30 group-hover:bg-[#0A0A0A]/45 transition-colors flex items-center justify-center">
          <span className="w-16 h-16 bg-[#FF2E63] text-[#FFF8E7] flex items-center justify-center">
            <Play className="w-7 h-7 ml-1" fill="currentColor" />
          </span>
        </div>
      </button>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {film.length && (
            <span className="text-xs uppercase tracking-[0.14em] font-bold px-2 py-0.5 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A]">{film.length}</span>
          )}
          <span className="text-xs uppercase tracking-[0.16em] text-[#1A5DFF] font-bold">{film.year} · {film.duration}</span>
        </div>
        <h3 className="font-heading font-bold text-xl text-[#0A0A0A] leading-snug">{film.title}</h3>
        <div className="text-sm text-[#2C2C2C]">Dir. {film.director}</div>
        <p className="text-[#2C2C2C] leading-relaxed text-sm flex-1">{film.description}</p>
        {(film.genres?.length > 0 || film.tags?.length > 0) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {film.genres?.map((g) => (
              <span key={`g-${g}`} className="text-xs uppercase tracking-[0.14em] px-2 py-1 bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A]" data-testid={`film-genre-${film.id}-${g}`}>{g}</span>
            ))}
            {film.tags?.map((t) => (
              <span key={`t-${t}`} className="text-xs uppercase tracking-[0.14em] px-2 py-1 bg-[#FFE8A8] text-[#0A0A0A] border-2 border-[#0A0A0A]">{t}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function VideoModal({ film, onClose }) {
  useEffect(() => {
    if (!film) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [film]);

  if (!film) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Playing ${film.title}`}
      className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="film-modal"
    >
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">Now playing</div>
            <div className="font-heading font-bold text-2xl text-[#FFF8E7]">{film.title}</div>
            <div className="text-sm text-[#FFE8A8]">Dir. {film.director} · {film.year}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close video"
            data-testid="film-modal-close"
            className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] p-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-video bg-black border-2 border-[#FF2E63]">
          <iframe
            title={film.title}
            src={`https://www.youtube-nocookie.com/embed/${film.youtube_id}?rel=0&modestbranding=1&autoplay=0`}
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}

export default function Films() {
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [length, setLength] = useState("All");
  const [genres, setGenres] = useState([]);  // multi-select; empty == any
  const [year, setYear] = useState("All");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/films`);
        setFilms(r.data || []);
      } catch (e) {
        setError("Could not load films. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(films.map((f) => f.year).filter(Boolean)));
    years.sort((a, b) => b - a);
    return years;
  }, [films]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return films.filter((f) => {
      if (q) {
        const haystack = [f.title, f.director, f.producer].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (length !== "All" && f.length !== length) return false;
      if (year !== "All" && String(f.year) !== String(year)) return false;
      if (genres.length > 0) {
        const fg = f.genres || [];
        const matches = genres.some((g) => fg.includes(g));
        if (!matches) return false;
      }
      return true;
    });
  }, [films, length, genres, year, search]);

  const toggleGenre = (g) => setGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]);
  const clearAll = () => { setLength("All"); setGenres([]); setYear("All"); setSearch(""); };

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-films">
      {/* HERO — matches Landing pop-art style */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]" data-testid="films-hero">
        <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">The programme</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95]">
            Films by<br />
            <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">neurodivergent artists.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            Free to watch, captioned where possible, never auto-playing. Click a poster to open the film in our embedded player.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/films/submit"
              data-testid="films-submit-cta"
              className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] pop-shadow rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Submit your film <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Search the programme</div>
              <div className="relative max-w-xl">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0A0A0A]" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by film title or filmmaker…"
                  aria-label="Search films by title or filmmaker name"
                  data-testid="film-search-input"
                  className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] pop-shadow rounded-none pl-12 pr-12 py-3 text-sm placeholder:text-[#2C2C2C]/60 focus:outline-none focus:ring-2 focus:ring-[#FF2E63]"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    data-testid="film-search-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 bg-[#FFD93D] border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#FF2E63] hover:text-[#FFF8E7] focus:outline-none focus:ring-2 focus:ring-[#FF2E63]"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Filter by length</div>
                <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter films by length" data-testid="film-length-filters">
                  {LENGTHS.map((l) => {
                    const isActive = l === length;
                    return (
                      <button
                        key={l}
                        role="tab"
                        aria-selected={isActive}
                        data-testid={`filter-length-${l.toLowerCase().replace(/[\s/]+/g, "-")}`}
                        onClick={() => setLength(l)}
                        className={`px-5 py-2.5 text-sm font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63] ${
                          isActive
                            ? "bg-[#FF2E63] text-[#FFF8E7] pop-shadow"
                            : "bg-[#FFF8E7] text-[#0A0A0A] hover:bg-[#FFD93D]"
                        }`}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Filter by year</div>
                <div className="relative">
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    data-testid="film-year-filter"
                    className="w-full appearance-none bg-[#FFD93D] text-[#0A0A0A] border-2 border-[#0A0A0A] pop-shadow px-4 py-2.5 pr-10 text-sm font-bold uppercase tracking-[0.12em] focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none"
                  >
                    <option value="All">All years</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <svg aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A0A0A]" viewBox="0 0 20 20" fill="currentColor"><path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" /></svg>
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Filter by genre {genres.length > 0 && <span className="ml-2 inline-flex items-center justify-center text-[10px] bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] px-1.5 py-0.5">{genres.length}</span>}</div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter films by genre" data-testid="film-genre-filters">
                {FILM_GENRES.map((g) => {
                  const isActive = genres.includes(g);
                  return (
                    <button
                      key={g}
                      aria-pressed={isActive}
                      data-testid={`filter-genre-${g.toLowerCase().replace(/[\s/]+/g, "-")}`}
                      onClick={() => toggleGenre(g)}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-all focus:outline-none focus:ring-2 focus:ring-[#FF2E63] ${
                        isActive
                          ? "bg-[#1A5DFF] text-[#FFF8E7]"
                          : "bg-[#FFF8E7] text-[#0A0A0A] hover:bg-[#FFD93D]"
                      }`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
              {genres.length > 0 && (
                <div className="mt-2 text-xs text-[#2C2C2C]">Showing films that match <strong>any</strong> selected genre.</div>
              )}
            </div>

            <div className="text-sm text-[#2C2C2C] flex flex-wrap items-center gap-3" data-testid="films-count">
              <span>{filtered.length} {filtered.length === 1 ? "film" : "films"} · sorted newest first</span>
              {(length !== "All" || year !== "All" || genres.length > 0 || search.trim()) && (
                <button onClick={clearAll} data-testid="filters-clear-all" className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A] underline underline-offset-2 hover:text-[#FF2E63]">Clear all filters</button>
              )}
            </div>
          </div>

          {loading && <div className="text-[#2C2C2C]" data-testid="films-loading">Loading the programme…</div>}
          {error && <div className="text-[#FF2E63]" data-testid="films-error">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="text-[#2C2C2C]" data-testid="films-empty">No films match these filters.</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="films-grid">
            {filtered.map((f) => (
              <FilmCard key={f.id} film={f} onPlay={setActive} />
            ))}
          </div>
        </div>
      </section>

      <VideoModal film={active} onClose={() => setActive(null)} />
    </div>
  );
}
